import React, { Component } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import Chrome from "@zendesk/knowledge-chrome/lib/Chrome";
import { ContentView, Sidebar } from "@zendesk/guide-chrome";
import {
  getCurrentBrandId,
  hasFeature,
  getHelpCenter,
} from "@zendesk/knowledge-context";
import t from "@zendesk/client-i18n-tools";
import { Button } from "@zendeskgarden/react-buttons";
import Breadcrumbs from "@zendesk/knowledge-components/lib/Breadcrumbs";
import { DragDropContext } from "react-beautiful-dnd";
import createBrandedUrl from "@zendesk/knowledge-utils/lib/createBrandedUrl";
import * as actions from "../../actions/category";
import * as sectionActions from "../../actions/section";
import ErrorBoundary from "../ErrorBoundary";
import SectionList from "../SectionList";
import LoadingOverlay from "../LoadingOverlay";
import Empty from "../Empty";
import Notifications from "../Notifications";
import { pathForBreadcrumb } from "../../utils/pathForBreadcrumb";
import styles from "./index.css";

export class Category extends Component {
  constructor(props) {
    super(props);

    this.onPageSelected = this.onPageSelected.bind(this);
    this.openNewSectionPage = this.openNewSectionPage.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onBreadcrumbClick = this.onBreadcrumbClick.bind(this);
    this.onMoveToTop = this.onMoveToTop.bind(this);
  }

  componentWillMount() {
    const { dispatch, sectionPage, categoryId } = this.props;
    dispatch(actions.fetchCategory(categoryId, sectionPage));
  }

  openNewSectionPage() {
    const { categoryId } = this.props;
    window.open(
      `/knowledge/sections/new?brand_id=${getCurrentBrandId()}&category_id=${categoryId}`,
      "_blank"
    );
  }

  onPageSelected(page) {
    const { dispatch, categoryId } = this.props;

    dispatch(actions.fetchSections(categoryId, { page }));

    dispatch(
      push({
        pathname: `/knowledge/arrange/categories/${categoryId}/page/${page}`,
        search: `?brand_id=${getCurrentBrandId()}`,
      })
    );
  }

  onDragEnd(dragEnd) {
    const { combine, source, destination, type, draggableId } = dragEnd;
    const { dispatch, categoryId } = this.props;

    const isPositioning =
      destination &&
      !(
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      );

    if (!isPositioning && !combine) {
      return;
    }

    if (combine) {
      dispatch(
        sectionActions.moveSectionToSection(
          draggableId, // moved section
          combine.draggableId, // destination section
          source.index
        )
      );
    } else if (type === "section" && destination.droppableId === "sections") {
      dispatch(
        actions.positionSection(
          categoryId,
          draggableId,
          source.index,
          destination.index
        )
      );
    }
  }

  onBreadcrumbClick(item) {
    const { dispatch } = this.props;

    dispatch(
      push({
        pathname: pathForBreadcrumb(item),
        search: `?brand_id=${getCurrentBrandId()}`,
      })
    );
  }

  onMoveToTop(section, sourceIndex) {
    const { dispatch } = this.props;

    dispatch(actions.moveSectionToTop(section, sourceIndex));
  }

  renderBreadcrumbs() {
    const { breadcrumbs, isFetching } = this.props;

    if (isFetching) {
      return null;
    }

    return (
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={this.onBreadcrumbClick}
      />
    );
  }

  renderContent() {
    const { isFetching, sectionCount, sectionPage } = this.props;

    if (isFetching) {
      return null;
    }

    if (sectionCount === 0) {
      return (
        <Empty testId="empty-category">
          <Empty.Title>{t("knowledge-arrange.category.empty")}</Empty.Title>
          <Empty.Description>
            {t("knowledge-arrange.category.emptyDescription")}
          </Empty.Description>
          <Empty.Actions>
            <Button
              primary
              data-test-id="empty-add-section"
              size="small"
              onClick={this.openNewSectionPage}
            >
              {t("knowledge-arrange.sections.addSection")}
            </Button>
          </Empty.Actions>
        </Empty>
      );
    }

    return (
      <SectionList
        onMoveToTop={this.onMoveToTop}
        onPageSelected={this.onPageSelected}
        page={sectionPage}
        count={sectionCount}
      />
    );
  }

  render() {
    const { hasCommunity } = this.props;
    const hc = getHelpCenter();

    return (
      <Chrome
        brandId={getCurrentBrandId()}
        dir={t.dir}
        topNav={
          <Chrome.Breadcrumbs>
            <Chrome.Breadcrumbs.Item>
              <a href={`/knowledge/arrange?brand_id=${getCurrentBrandId()}`}>
                {t("knowledge-arrange.breadcrumbs.arrangeContent")}
              </a>
            </Chrome.Breadcrumbs.Item>
            <Chrome.Breadcrumbs.Separator />
            <Chrome.Breadcrumbs.Item>
              {t("knowledge-arrange.name")}
            </Chrome.Breadcrumbs.Item>
          </Chrome.Breadcrumbs>
        }
      >
        <Sidebar>
          <Sidebar.Item active testId="sidebar-user-segments-link">
            {t("knowledge-arrange.name")}
          </Sidebar.Item>

          {hasCommunity && (
            <Sidebar.LinkItem
              href={createBrandedUrl(
                hc.route.subdomain,
                "/hc/admin/arrange_contents#community"
              )}
              testId="sidebar-arrange-topics-link"
            >
              {t("knowledge-arrange.sidebar.arrangeTopics")}
            </Sidebar.LinkItem>
          )}

          <Sidebar.LinkItem
            href={createBrandedUrl(
              hc.route.subdomain,
              "/hc/admin/import_articles"
            )}
            testId="sidebar-import-articles-link"
          >
            {t("knowledge-arrange.sidebar.importArticles")}
          </Sidebar.LinkItem>
        </Sidebar>

        <ContentView>
          <ErrorBoundary>
            <Notifications dir={t.dir} />
            <div className={styles.appHeader}>
              <h1 className={styles.title}>{t("knowledge-arrange.name")}</h1>
            </div>

            {this.renderBreadcrumbs()}

            <DragDropContext onDragEnd={this.onDragEnd}>
              {this.renderContent()}
            </DragDropContext>

            <LoadingOverlay />
          </ErrorBoundary>
        </ContentView>
      </Chrome>
    );
  }
}

Category.propTypes = {
  dispatch: PropTypes.func,
  categoryId: PropTypes.string.isRequired,
  isFetching: PropTypes.bool.isRequired,
  sectionCount: PropTypes.number.isRequired,
  sectionPage: PropTypes.number.isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  hasCommunity: PropTypes.bool,
};

const mapStateToProps = ({ category, sections }, { match }) => {
  const page = parseInt(match.params.page, 10);

  return {
    categoryId: match.params.id,
    isFetching: category.isFetching,
    sectionCount: category.sectionCount,
    sectionPage: isNaN(page) ? 1 : page,
    breadcrumbs: category.breadcrumbs,
    hasCommunity: hasFeature("community") && getHelpCenter().community,
  };
};

export default connect(mapStateToProps)(Category);



// WEBPACK FOOTER //
// ./src/components/Category/index.js